import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2, Zap } from 'lucide-react'

import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { fetchProducts } from '@/lib/api/products'
import {
  createPosQuickKey,
  deletePosQuickKey,
  fetchMasterPosQuickKeys,
  fetchPosQuickKeysList,
  fetchPosQuickKeysPage,
  MAX_POS_QUICK_KEYS,
  POS_QUICK_KEY_ITEM_TYPES,
  updatePosQuickKey,
} from '@/lib/api/posQuickKeys'
import { fetchServices } from '@/lib/api/services'
import { cn } from '@/lib/utils'

function emptyForm() {
  return {
    sort: 0,
    label: '',
    item_type: 'service',
    service_id: '',
    product_id: '',
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').PosQuickKey} key
 */
function keyToForm(key) {
  return {
    sort: key.sort ?? 0,
    label: key.label ?? '',
    item_type: key.item_type ?? 'service',
    service_id: key.service_id ?? '',
    product_id: key.product_id ?? '',
    is_active: Boolean(key.is_active),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

function formatItemType(type) {
  return POS_QUICK_KEY_ITEM_TYPES.find((option) => option.value === type)?.label ?? type
}

const EXPORT_COLUMNS = [
  { key: 'display_name', label: 'Label', getValue: (row) => row.display_name ?? '' },
  { key: 'item_type', label: 'Type', getValue: (row) => row.item_type },
  { key: 'sort', label: 'Sort', getValue: (row) => row.sort },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function PosQuickKeysPage() {
  const { activeBranchId } = useBranch()
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState(emptyForm())
  const [reloadKey, setReloadKey] = useState(0)
  const [services, setServices] = useState([])
  const [products, setProducts] = useState([])
  const [keyCount, setKeyCount] = useState(0)

  const fetchPage = useCallback((params) => fetchPosQuickKeysPage(params), [])

  useEffect(() => {
    let active = true

    async function loadDropdowns() {
      try {
        const [serviceItems, productItems] = await Promise.all([
          fetchServices(),
          fetchProducts(),
        ])
        if (!active) return
        setServices(serviceItems.filter((item) => item.is_active))
        setProducts(productItems.filter((item) => item.is_active))
      } catch {
        if (!active) return
        setServices([])
        setProducts([])
      }
    }

    loadDropdowns()
    return () => {
      active = false
    }
  }, [activeBranchId])

  useEffect(() => {
    let active = true

    async function loadKeyCount() {
      try {
        const keys = await fetchMasterPosQuickKeys()
        if (active) setKeyCount(keys.length)
      } catch {
        if (active) setKeyCount(0)
      }
    }

    loadKeyCount()
    return () => {
      active = false
    }
  }, [activeBranchId, reloadKey])

  function openCreate() {
    if (keyCount >= MAX_POS_QUICK_KEYS) {
      setError(`Maximum ${MAX_POS_QUICK_KEYS} quick keys per branch.`)
      return
    }
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm())
    setError('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').PosQuickKey} key
   */
  function openEdit(key) {
    setCreating(false)
    setSelectedId(key.id)
    setForm(keyToForm(key))
    setError('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting) return
    setModalOpen(false)
    setError('')
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')

    const payload = {
      sort: Number(form.sort) || 0,
      label: form.label.trim() || null,
      item_type: form.item_type,
      service_id: form.item_type === 'service' ? Number(form.service_id) : null,
      product_id: form.item_type === 'product' ? Number(form.product_id) : null,
      is_active: form.is_active,
    }

    try {
      if (creating) {
        await createPosQuickKey(payload)
      } else if (selectedId) {
        await updatePosQuickKey(selectedId, payload)
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      setError(
        getFieldError(err, 'service_id')
          ?? getFieldError(err, 'product_id')
          ?? getFieldError(err, 'quick_key')
          ?? err?.response?.data?.message
          ?? 'Unable to save quick key.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (!selectedId || creating) return
    setSubmitting(true)
    setError('')

    try {
      await deletePosQuickKey(selectedId)
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to delete quick key.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Zap className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">POS quick keys</h1>
            <p className="text-sm text-muted-foreground">
              Configure one-tap buttons on the POS Quick keys tab (max {MAX_POS_QUICK_KEYS} per branch).
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={openCreate}
          disabled={keyCount >= MAX_POS_QUICK_KEYS}
        >
          <Plus className="h-4 w-4" />
          New quick key
        </Button>
      </div>

      <MasterServerTable
        key={activeBranchId}
        fetchPage={fetchPage}
        reloadKey={reloadKey}
        getRowKey={(key) => key.id}
        onEdit={openEdit}
        emptyLabel="No quick keys yet."
        searchPlaceholder="Search quick keys…"
        exportColumns={EXPORT_COLUMNS}
        exportFilename="pos-quick-keys.csv"
        fetchAllForExport={(params) => fetchPosQuickKeysList(params)}
        columns={[
          {
            key: 'display_name',
            label: 'Label',
            sortable: true,
            render: (row) => row.display_name ?? '—',
          },
          {
            key: 'item_type',
            label: 'Type',
            sortable: true,
            render: (row) => formatItemType(row.item_type),
          },
          { key: 'sort', label: 'Sort', sortable: true, render: (row) => row.sort },
          {
            key: 'is_active',
            label: 'Status',
            render: (row) => (row.is_active ? 'Active' : 'Inactive'),
          },
        ]}
      />

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        title={creating ? 'New quick key' : 'Edit quick key'}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="quick-key-type" className="mb-1.5 block text-sm font-medium">
                Item type
              </label>
              <select
                id="quick-key-type"
                value={form.item_type}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    item_type: event.target.value,
                    service_id: '',
                    product_id: '',
                  }))
                }
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                {POS_QUICK_KEY_ITEM_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="quick-key-sort" className="mb-1.5 block text-sm font-medium">
                Sort order
              </label>
              <input
                id="quick-key-sort"
                type="number"
                min={0}
                value={form.sort}
                onChange={(event) => updateField('sort', event.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          </div>

          <div>
            <label htmlFor="quick-key-label" className="mb-1.5 block text-sm font-medium">
              Button label (optional)
            </label>
            <input
              id="quick-key-label"
              value={form.label}
              onChange={(event) => updateField('label', event.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              placeholder="Defaults to service/product name"
            />
          </div>

          {form.item_type === 'service' ? (
            <div>
              <label htmlFor="quick-key-service" className="mb-1.5 block text-sm font-medium">
                Service
              </label>
              <select
                id="quick-key-service"
                value={form.service_id}
                onChange={(event) => updateField('service_id', event.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              >
                <option value="">Select service…</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} ({service.duration} min)
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label htmlFor="quick-key-product" className="mb-1.5 block text-sm font-medium">
                Product
              </label>
              <select
                id="quick-key-product"
                value={form.product_id}
                onChange={(event) => updateField('product_id', event.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              >
                <option value="">Select product…</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(event) => updateField('is_active', event.target.checked)}
              className="rounded border-border"
            />
            Quick key is active on POS
          </label>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-between gap-2">
            {!creating && selectedId ? (
              <Button type="button" variant="outline" disabled={submitting} onClick={handleDelete}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : creating ? 'Create quick key' : 'Save quick key'}
              </Button>
            </div>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
